<template>
  <calendar-grid
    class="week"
    :class="{
      top: first,
      bottom: last,
    }"
    :start="weekDays[0].date"
    :end="weekDays[weekDays.length - 1].date"
    offset-left="var(--group-size)"
  >
    <div class="controls">
      <slot name="controls"></slot>
    </div>

    <div
      v-for="{ number, name, current } in weekDays"
      :key="`overview__${name}__${number}`"
      class="overview"
    >
      <slot name="overview" :day="name" :number="number" :current="current"></slot>
    </div>

    <calendar-grid
      class="week-events"
      :start="weekDays[0].date"
      :end="weekDays[weekDays.length - 1].date"
    >
      <slot name="events" />
    </calendar-grid>

    <day
      v-for="{ date, number, name, current } in weekDays"
      :key="`day__${name}__${number}`"
      :name="name"
      :current="current"
      @drag-end="onDrop(date)"
    >
      <slot name="day" :day="name" :number="number" :current="current"></slot>
    </day>
  </calendar-grid>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import moment from 'moment'
import Day from '@/components/weekly-schedule/calendar/Day.vue'
import { WeekDays } from '@/components/weekly-schedule/utils'
import CalendarGrid from '@/components/weekly-schedule/CalendarGrid.vue'

export default defineComponent({
  name: 'Week',

  components: {
    CalendarGrid,
    Day,
  },

  props: {
    first: { type: Boolean, default: false },
    last: { type: Boolean, default: false },
    weekDays: {
      type: Array as unknown as PropType<WeekDays>,
      required: true,
    },
  },

  computed: {
    weekStart() {
      return this.weekDays[0].date
    },
  },

  methods: {
    onDrop(date: moment.Moment) {
      this.$emit('drop', date)
    },
  },
})
</script>

<style scoped>
.week {
  margin-left: var(--space-base);
  margin-right: var(--space-base);
  --radius: var(--space-small);
  --column-size: minmax(96px, 1fr);
  --group-size: 260px;
  --week-start: sun-start;
  --week-end: sat-end;

  background-color: var(--color-white);
  border: 1px solid var(--color-grey--lighter);
  position: relative;
  min-height: 0;
}

.week-events:empty {
  display: none;
}

.week-events {
  position: relative;
  grid-column-start: var(--week-start);
  grid-column-end: var(--week-end);
  grid-auto-flow: column;
  padding: var(--space-smaller) 0;
}

.week:not(.top) {
  border-top: none;
}

.week.top {
  border-top-right-radius: var(--radius);
  border-top-left-radius: var(--radius);
  border-bottom: var(--space-smaller) solid var(--color-grey--lighter);
  position: sticky;
  top: 90px; /* height of the secondary toolbar */
  z-index: var(--elevation-week-view-resource-bar);
}

.week.top .controls {
  background-color: var(--color-white);
  border-top-left-radius: var(--radius);
}

.week.bottom {
  border-bottom-right-radius: var(--radius);
  border-bottom-left-radius: var(--radius);
  margin-bottom: var(--space-base);
}

.week.bottom .controls {
  border-bottom-left-radius: var(--radius);
}

.controls {
  position: sticky;
  left: 0;
  grid-column: 1;
  grid-row-end: span 100;
  min-height: 0;
}

.quick-filters {
  grid-column: 1/-1;
  position: sticky;
  left: 0;
  background-color: var(--color-white);
  z-index: var(--elevation-week-view-controls);
  min-height: 0;
}

.week.top .week-events {
  max-height: 20vh;
  margin-right: calc(-1 * var(--space-small));
  overflow-y: scroll;
}

.week-events::-webkit-scrollbar {
  width: var(--space-small);
}

.week-events::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: var(--color-grey--light);
}

.overview {
  font-family: var(--font-regular);
  font-size: 11px;
  color: var(--color-grey);
  text-align: center;
  margin: 0;
  padding: var(--space-smaller) var(--space-smallest) 0;
  z-index: var(--elevation-base);
  position: relative;
  pointer-events: none;
  font-style: italic;
}
</style>
